import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import { TOURS_GROUP_TYPE, TOURS_GROUP_TYPES_LIST } from 'constants/tours'
import config from 'constants/config'
import TourPageTailorMadeTourCategoryTile from 'components/Tours/TourPageTailorMadeTourCategoryTile'
import TourPageSearchTourCategoryTile from 'components/Tours/TourPageSearchTourCategoryTile'
import { LE_AGENT_HUB, LE_AGENT_HUB_CVFR, LUXURY_ESCAPES } from 'constants/brands'
import tourGlossaryMessages from 'hooks/Tours/tourGlossaryMessages'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
const SIGNATURE_SERIES_BRANDS = new Set([LUXURY_ESCAPES, LE_AGENT_HUB, LE_AGENT_HUB_CVFR])

// Tiles with `parameterName: 'categories'` are gated on the search facets API response
// (see the `tourFacets.categories.has(...)` check below), so `parameterValue` must match the
// slug the API returns *exactly* or the tile silently self-hides. The canonical form is
// snake_case, sourced from `/api/search/tour/v1/facet/list`. Do not derive these by hand:
// the labels do not map mechanically (e.g. "Health & Wellness" is `health_wellness`, not
// `health_and_wellness`), and `slugify` will not correct a wrong value because `isSlug`
// treats a bare lowercase run such as `healthandwellness` as already valid. Verify any new
// or changed slug against that endpoint.
export function getCategories(intl: IntlShape): Array<App.Tours.CategoryData> {
  return [
    {
      id: 'signature_series',
      name: intl.formatMessage(tourGlossaryMessages.signatureSeriesTours),
      subText: intl.formatMessage(
        {
          id: 'LAEK-tYgLxUhGKCAJbNv',
          defaultMessage: 'An experience like no other',
          description: 'Subtext for the Signature Series tour category in the tours section',
        },
      ),
      imageId: 'tour-category-tile-signature-serie',
      show: SIGNATURE_SERIES_BRANDS.has(config.BRAND),
      parameterName: isAgentHubEnabled ? 'tourStyles' : 'link',
      parameterValue: isAgentHubEnabled ? ['signature_series'] : '/campaign/signature-series',
    },
    {
      id: 'tailor_made_tours',
      name: intl.formatMessage(
        {
          id: 'KD8oHCltMVmV5e9n1nmX',
          defaultMessage: 'Tailor Made Tours',
          description: 'Tour category name for custom tailor-made tours in the tours section',
        },
      ),
      subText: '',
      imageId: 'tour-category-tile-tailor-made-tour',
      show: config.TAILOR_MADE_TOURS_ENABLED,
    },
    {
      id: 'deluxe_tours',
      name: intl.formatMessage(tourGlossaryMessages.deluxeTours),
      subText: intl.formatMessage(
        {
          id: 'uJDGNWpoYj3sk1dyowJB',
          defaultMessage: 'Discover the highlights, effortlessly',
          description: 'Subtext for the Deluxe Tours category on the tour offer detail page',
        },
      ),
      imageId: 'tour-category-tile-deluxe',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['deluxe'],
    },
    {
      id: 'premium_tours',
      name: intl.formatMessage(tourGlossaryMessages.premiumTours),
      subText: intl.formatMessage(
        {
          id: 'SFkM3XlK4Zh2iAuZuoFc',
          defaultMessage: 'Elevated experiences, elevated accommodation',
          description: 'Subtext for the Premium Tours category on the tour offer detail page',
        },
      ),
      imageId: 'tour-category-tile-premium-v2',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['premium'],
    },
    {
      id: 'ultra_lux_tours',
      name: intl.formatMessage(tourGlossaryMessages.ultraLuxTours),
      subText: intl.formatMessage(
        {
          id: 'gmz1rL56AldK1Hv5z83g',
          defaultMessage: 'Only the best, from start to finish',
          description: 'Subtext for the Ultra Lux Tours category on the tour page',
        },
      ),
      imageId: 'tour-category-tile-ultra-lux-v2',
      show: true,
      parameterName: 'tourStyles',
      parameterValue: ['ultralux'],
    },
    {
      id: 'private_journeys',
      name: intl.formatMessage(
        {
          id: '55qwLBdDDAP4puU0irv9',
          defaultMessage: 'Private Tours',
          description: 'Tour category name for private tours on the tour page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'aEFJFAb4CwbQETjRT1d8',
          defaultMessage: 'An intimate journey with just you and your partner',
          description: 'Subtext for the Private Journeys category in the tours section',
        },
      ),
      imageId: 'tour-category-tile-private-journey',
      show: true,
      // Private tours are a marketing entry point and must remain available even when no matching facets are returned.
      showWithoutFacet: true,
      parameterName: TOURS_GROUP_TYPE.isPrivateRequest,
      parameterValue: true,
    },
    {
      id: 'widlife_safari',
      name: intl.formatMessage(
        {
          id: 'm9Egk0vMyrVD0vprdAau',
          defaultMessage: 'Wildlife Safari',
          description: 'Tour category name for wildlife safari tours on the tour page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'wuyXtrevCqDAQRgQEwUq',
          defaultMessage: 'Roam amongst diverse landscapes & wildlife',
          description: 'Subtext for the Wildlife Safari category in the tours section',
        },
      ),
      imageId: 'l0vkpbn0gnr2fe3nivq',
      show: true,
      parameterName: 'categories',
      parameterValue: ['wildlife_safaris'],
    },
    {
      id: 'rail_journeys',
      name: intl.formatMessage(
        {
          id: 'x2JPFB236I8dXoZDseGi',
          defaultMessage: 'Rail Journeys',
          description: 'Tour category name for rail journey tours on the tour page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'Khvocc9BsW4n13BxuNXw',
          defaultMessage: 'All aboard to see the sights',
          description: 'Subtext for the Rail Journeys category in the tours section',
        },
      ),
      imageId: 'cbwpojiatq0ernz9nc8i',
      show: true,
      parameterName: 'categories',
      parameterValue: ['rail_journeys'],
    },
    {
      id: 'sailing',
      name: intl.formatMessage(
        {
          id: '8R0yp3WZP4kEkkdWB0fq',
          defaultMessage: 'Sailing',
          description: 'Tour category name for sailing tours on the tour offer detail page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'Zco9igKgHTfL94UQNnJm',
          defaultMessage: 'Hop on board to explore the seas',
          description: 'Subtext for the Sailing category in the tours section',
        },
      ),
      imageId: '1bf7sxfr2y53y9y15b',
      show: true,
      parameterName: 'categories',
      parameterValue: ['sailing'],
    },
    {
      id: 'food_and_culinary',
      name: intl.formatMessage(
        {
          id: 'WGWchTXtYQs80kBayhOy',
          defaultMessage: 'Food & Culinary',
          description: 'Tour category name for food and culinary tours in the tours section',
        },
      ),
      subText: intl.formatMessage(
        {
          id: '4nV6v5t14bKqT9C72A8S',
          defaultMessage: 'Taste your way around the world',
          description: 'Subtext for the Food & Culinary category in the tours section',
        },
      ),
      imageId: 'tours-kbyg-africa-cash',
      show: true,
      parameterName: 'categories',
      parameterValue: ['food_and_culinary_tours'],
    },
    {
      id: 'cultural',
      name: intl.formatMessage(
        {
          id: 'pgl5nkmo5DOhy2D_ggUI',
          defaultMessage: 'Cultural',
          description: 'Tour category name for cultural tours on the tour offer detail page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'AiQEjZIaw7QJlCodLKne',
          defaultMessage: 'See the sights & dive into culture',
          description: 'Subtext for the Cultural tours category on the tour offer detail page',
        },
      ),
      imageId: 'tour-category-tile-cultural',
      show: false,
      parameterName: 'categories',
      parameterValue: ['cultural_tours'],
    },
    {
      id: 'lux_exclusive',
      name: intl.formatMessage(
        {
          id: 'TxlMMKgor5yZq-d8fA7x',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: '_eHPOwIVxFFL0QQdjHdg',
          defaultMessage: 'Exclusive to Luxury Escapes',
          description: 'Subtext for the Lux Exclusive category, indicating tours only available through Luxury Escapes',
        },
      ),
      imageId: 'tour-category-tile-lux-exclusive',
      show: false,
      parameterName: 'categories',
      parameterValue: ['lux_exclusive'],
    },
    {
      id: 'family_friendly',
      name: intl.formatMessage(
        {
          id: '4cC76yFWL3Vj4HwIHKDa',
          defaultMessage: 'Family Friendly',
          description: 'Tour category name for family-friendly tours on the tour page',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'xqrqPfFRzTpBd0hujU53',
          defaultMessage: 'Adventures for the whole family',
          description: 'Subtext for the Family Friendly category in the tours section',
        },
      ),
      imageId: 'tour-category-tile-family',
      show: false,
      parameterName: TOURS_GROUP_TYPE.isFamilyFriendly,
      parameterValue: true,
    },
    {
      id: 'hiking_cycling_adventure',
      name: intl.formatMessage(
        {
          id: 'dQZeL2v8_GZBkkBYXGkx',
          defaultMessage: 'Hiking, Cycling & Adventure',
          description: 'Tour category name for hiking, cycling and adventure tours',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'YO4G0jPjBFzgGlJoRf7x',
          defaultMessage: 'Explore the great outdoors',
          description: 'Subtext for the Hiking, Cycling & Adventure category',
        },
      ),
      imageId: 'tour-category-tile-adventure',
      show: false,
      parameterName: 'categories',
      parameterValue: ['hiking_cycling_and_adventure'],
    },
    {
      id: 'festivals_and_events',
      name: intl.formatMessage(
        {
          id: 'jLwuaxJAbd-p4EAGPSAr',
          defaultMessage: 'Festivals & Events',
          description: 'Tour category name for festivals and events tours in the tours section',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'VK3Wbgl9sPv6bizpopVe',
          defaultMessage: 'Don’t miss out on life’s best moments',
          description: 'Subtext for the Festivals & Events category in the tours section',
        },
      ),
      imageId: 'tour-category-tile-festival',
      show: false,
      parameterName: 'categories',
      parameterValue: ['festivals_and_events'],
    },
    {
      id: 'health_and_wellness',
      name: intl.formatMessage(
        {
          id: 'D7g2myM2IUMbXfgWNwa3',
          defaultMessage: 'Health & Wellness',
          description: 'Tour category name for health and wellness tours in the tours section',
        },
      ),
      subText: intl.formatMessage(
        {
          id: 'hf_xEF45HQ5rEqSqN-ni',
          defaultMessage: 'Relax & rejuvenate',
          description: 'Subtext for the Health & Wellness category in the tours section',
        },
      ),
      imageId: 'tour-category-tile-health',
      show: false,
      parameterName: 'categories',
      parameterValue: ['health_wellness'],
    },
  ]
}

interface Props {
  title?: React.ReactNode
}

function TourPageCategoriesCarousel(props: Props) {
  const { title } = props
  const intl = useIntl()

  const tourFacets = useTourFacetLocationCategory({})

  const allCategories = useMemo(() => getCategories(intl), [intl])

  const categories = useMemo(() => {
    return allCategories.filter(category => {
      if (category.show) {
        if (category.showWithoutFacet) {
          return true
        }
        if (category.parameterName === 'link') {
          return !!category.parameterValue
        } else if (category.parameterName === 'categories') {
          return tourFacets.categories.has(category.parameterValue[0])
        } else if (category.parameterName === 'productTypes') {
          return tourFacets.productTypes.has(category.parameterValue)
        } else if (category.parameterName === 'tourStyles') {
          return tourFacets.tourStyles.has(category.parameterValue[0])
        } else if (TOURS_GROUP_TYPES_LIST.includes(category.parameterName as TOURS_GROUP_TYPE)) {
          return tourFacets.groupTypes.has(category.parameterName ?? '')
        }
        return true
      }
      return false
    })
  }, [tourFacets, allCategories])

  return <VerticalSpacer gap={20}>
    <LayoutContainer>
      <Heading variant="heading2">
        {title ?? (
          <FormattedMessage
            id="dDcT1LGpw9MfSy_RWhoG"
            defaultMessage="Explore <i>your</i> way"
            description="Default heading for the tour categories carousel, with 'your' in italics"
            values={{ i: (chunks) => <i>{chunks}</i> }}
          />
        )}
      </Heading>
    </LayoutContainer>
    <FullWidthCarousel snap="start" itemsPerArrow={6}>
      {categories.map(category => {
        if (category.id === 'tailor_made_tours') {
          return <TourPageTailorMadeTourCategoryTile key={category.id} category={category} />
        } else {
          return <TourPageSearchTourCategoryTile key={category.id} category={category} />
        }
      })}
    </FullWidthCarousel>
  </VerticalSpacer>
}

export default React.memo(TourPageCategoriesCarousel)
